import { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
    addEdge,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    Connection,
    Edge,
    Node,
    ReactFlowProvider,
    Panel,
    Handle,
    Position,
    getBezierPath,
    EdgeProps,
    BaseEdge,
    EdgeLabelRenderer,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Box,
    Button,
    Typography,
    AppBar,
    Toolbar,
    IconButton,
    CircularProgress,
    Paper,
    Stack,
    Divider,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Checkbox,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as BackIcon,
    Message as MessageIcon,
    AccountTree as MenuIcon,
    AltRoute as ConditionIcon,
    Close as CloseIcon,
    AddCircle as AddCircleIcon,
    Delete as DeleteIcon,
    CheckCircle as EndIcon,
    HelpOutline as QuestionIcon,
    Person as UserIcon,
    Groups as QueueIcon,
    StopCircle as StopBotIcon,
    Http as HttpIcon,
    Schedule as ScheduleIcon,
    AccessTime as AccessTimeIcon,
    PlayArrow as TestIcon,
    ContentCopy as CopyIcon,
    SettingsEthernet as IntegrationIcon,
    Bolt as TriggerIcon,
    Image as ImageIcon,
    MusicNote as MusicNoteIcon,
    VideoLibrary as VideoIcon,
    ViewHeadline as ContentIcon,
    RocketLaunch as RocketIcon,
    FileDownload as FileDownloadIcon,
    FileUpload as FileUploadIcon,
    Edit as EditIcon,
    PersonSearch as IdentifyClientIcon,
    HowToReg as IdentifyClientV2Icon,
    Receipt as ConsultarFaturasIcon,
    ReceiptLong as ConsultarFaturasV2Icon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Widgets as WidgetsIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';
import { FlowSimulator } from './FlowSimulator';
import { PlayArrow as PlayIcon } from '@mui/icons-material';
import SysEditorWhatsapp from '../../../components/syseditor/SysEditorWhatsapp';
import FlowBuilderSingleBlockModal from './FlowBuilderSingleBlockModal';
import { FLOW_BOT_VARIABLES } from './flowBotVariables';
import FlowBuilderConsultarFaturasModal, { CONSULTAR_FATURAS_DEFAULTS } from './FlowBuilderConsultarFaturasModal';
import FlowBuilderConsultarFaturasV2Modal, { CONSULTAR_FATURAS_V2_DEFAULTS } from './FlowBuilderConsultarFaturasV2Modal';
import FlowBuilderIdentifyClientV2Modal, { IDENTIFY_CLIENT_V2_DEFAULTS, IDENTIFY_CLIENT_V2_NATIVE_OPTIONS } from './FlowBuilderIdentifyClientV2Modal';
import React from 'react';

const FLOW_PALETTE_WIDTH = 260;
const FLOW_PALETTE_COLLAPSED_WIDTH = 48;

const onPaletteDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
};

// Função para renderizar texto formatado (negrito e itálico)
const renderFormattedText = (text: string): React.ReactNode => {
    if (!text) return null;

    // Função recursiva para processar formatação aninhada
    const processFormattedText = (content: string, key: number = 0): React.ReactNode => {
        // Procurar por ambas as formatações e pegar a primeira que aparece
        const boldRegex = /\*([^*]+)\*/;
        const italicRegex = /_([^_]+)_/;

        const boldMatch = content.match(boldRegex);
        const italicMatch = content.match(italicRegex);

        // Determinar qual formatação aparece primeiro
        let firstMatch: { type: 'bold' | 'italic'; match: RegExpMatchArray; index: number } | null = null;

        if (boldMatch && (!italicMatch || (boldMatch.index! < italicMatch.index!))) {
            firstMatch = { type: 'bold', match: boldMatch, index: boldMatch.index! };
        } else if (italicMatch) {
            firstMatch = { type: 'italic', match: italicMatch, index: italicMatch.index! };
        }

        if (firstMatch) {
            const beforeFormat = content.substring(0, firstMatch.index);
            const formatContent = firstMatch.match[1];
            const afterFormat = content.substring(firstMatch.index + firstMatch.match[0].length);

            const parts: React.ReactNode[] = [];

            // Processar texto antes da formatação
            if (beforeFormat) {
                parts.push(processFormattedText(beforeFormat, key * 10));
            }

            // Processar conteúdo da formatação (pode conter outra formatação aninhada)
            const nestedParts = processFormattedText(formatContent, key * 10 + 1);

            // Aplicar a formatação apropriada
            if (firstMatch.type === 'bold') {
                parts.push(
                    <strong key={`bold-${key}`}>
                        {nestedParts}
                    </strong>
                );
            } else {
                parts.push(
                    <em key={`italic-${key}`}>
                        {nestedParts}
                    </em>
                );
            }

            // Processar texto depois da formatação
            if (afterFormat) {
                parts.push(processFormattedText(afterFormat, key * 10 + 2));
            }

            return <>{parts}</>;
        }

        // Se não encontrou formatação, retornar texto normal
        return <React.Fragment key={`text-${key}`}>{content}</React.Fragment>;
    };

    // Quebrar o texto em linhas e processar cada uma
    const lines = text.split('\n');
    return (
        <>
            {lines.map((line, index) => (
                <React.Fragment key={index}>
                    {processFormattedText(line, index)}
                    {index < lines.length - 1 && <br />}
                </React.Fragment>
            ))}
        </>
    );
};

const EMOJI_NUMBERS = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];

const numberToEmoji = (n: number): string => {
    if (n >= 1 && n <= 9) return EMOJI_NUMBERS[n - 1]!;
    if (n === 10) return '🔟';
    return String(n);
};

const FLOW_HANDLE_SIZE = 12;

const flowEditorHandleSx = {
    '& .react-flow__handle': {
        width: `${FLOW_HANDLE_SIZE}px`,
        height: `${FLOW_HANDLE_SIZE}px`,
        minWidth: `${FLOW_HANDLE_SIZE}px`,
        minHeight: `${FLOW_HANDLE_SIZE}px`,
        border: '2px solid #fff',
        boxShadow: '0 0 0 1px #b1b1b7',
    },
};

// Componentes para Nós Customizados
const StartNodeComponent = () => (
    <Paper sx={{
        p: 1.5,
        minWidth: 140,
        border: '2px solid',
        borderColor: 'success.main',
        position: 'relative',
        bgcolor: '#e8f5e9',
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RocketIcon sx={{ color: 'success.main', fontSize: 22 }} />
            <Box>
                <Typography variant="caption" color="success.dark" fontWeight="bold">PONTO DE PARTIDA</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>Início do fluxo</Typography>
            </Box>
        </Box>
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

const MessageNodeComponent = ({ id, data }: any) => (
    <Paper sx={{
        p: 1,
        minWidth: 150,
        maxWidth: 250,
        border: '1px solid',
        borderColor: 'primary.main',
        position: 'relative',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    }}>
        <Handle type="target" position={Position.Top} />
        {id !== 'start' && (
            <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                    e.stopPropagation();
                    window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
                }}
                sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#ffebee' }, width: 20, height: 20 }}
            >
                <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MessageIcon sx={{ color: 'primary.main', fontSize: 18 }} />
            <Typography variant="caption" color="primary" fontWeight="bold">{data.label || 'Mensagem'}</Typography>
        </Box>
        <Typography variant="body2" sx={{
            mt: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
        }}>
            {data.text ? renderFormattedText(data.text) : 'Digite sua mensagem...'}
        </Typography>
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

const SingleBlockNodeComponent = ({ id, data }: any) => {
    const elements = data.elements || [];
    const seq = data.seq || [];

    const getIcon = (type: string) => {
        switch (type) {
            case 'message': return <MessageIcon sx={{ fontSize: 14 }} color="primary" />;
            case 'interval': return <ScheduleIcon sx={{ fontSize: 14 }} color="warning" />;
            case 'img':
            case 'image': return <ImageIcon sx={{ fontSize: 14 }} color="success" />;
            case 'audio': return <MusicNoteIcon sx={{ fontSize: 14 }} color="error" />;
            case 'video': return <VideoIcon sx={{ fontSize: 14 }} color="secondary" />;
            default: return <MessageIcon sx={{ fontSize: 14 }} />;
        }
    };

    return (
        <Paper sx={{
            p: 1,
            minWidth: 150,
            maxWidth: 250,
            border: '2px solid',
            borderColor: 'primary.dark',
            position: 'relative',
        }}>
            <Handle type="target" position={Position.Top} />
            <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                    e.stopPropagation();
                    window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
                }}
                sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#ffebee' }, width: 20, height: 20 }}
            >
                <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ContentIcon sx={{ color: 'primary.dark', fontSize: 18 }} />
                <Typography variant="caption" color="primary.dark" fontWeight="bold">{data.label || 'Conteúdo (Bloco)'}</Typography>
            </Box>

            <Stack spacing={0.5} sx={{ mt: 1 }}>
                {seq.map((elementId: string, index: number) => {
                    const element = elements.find((e: any) => e.number === elementId);
                    if (!element || index >= 5) return null;
                    return (
                        <Box key={elementId} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'action.hover',
                            p: 0.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            {getIcon(element.type)}
                            <Typography variant="caption" sx={{
                                ml: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '180px'
                            }}>
                                {element.type === 'interval' ? `${element.value}s` : (element.value || element.type)}
                            </Typography>
                        </Box>
                    );
                })}
                {seq.length > 5 && (
                    <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        + {seq.length - 5} itens...
                    </Typography>
                )}
                {seq.length === 0 && (
                    <Typography variant="caption" sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.disabled' }}>
                        Vazio...
                    </Typography>
                )}
            </Stack>
            <Handle type="source" position={Position.Bottom} />
        </Paper>
    );
};

const MenuNodeComponent = ({ id, data }: any) => (
    <Paper sx={{
        p: 1,
        minWidth: 150,
        maxWidth: 250,
        border: '1px solid',
        borderColor: 'secondary.main',
        position: 'relative',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#f3e5f5' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MenuIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
            <Typography variant="caption" color="secondary" fontWeight="bold">{data.label || 'Menu'}</Typography>
        </Box>
        <Typography variant="body2" sx={{
            mt: 1,
            mb: 1,
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
        }}>
            {data.message ? renderFormattedText(data.message) : 'Digite a pergunta...'}
        </Typography>

        <Stack spacing={0.5}>
            {(data.options || []).map((opt: any, i: number) => (
                <Box
                    key={i}
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'background.default',
                        p: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: 24
                    }}
                >
                    <Typography variant="caption" sx={{ flexGrow: 1 }}>
                        {numberToEmoji(i + 1)} {opt.label}
                    </Typography>
                    <Handle
                        type="source"
                        position={Position.Right}
                        id={`a${i + 1}`}
                        style={{ right: -14, top: '50%', background: '#9c27b0' }}
                    />
                </Box>
            ))}
        </Stack>

        {(!data.options || data.options.length === 0) && (
            <Handle type="source" position={Position.Bottom} />
        )}
    </Paper>
);

const ConditionNodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 150, border: '1px solid', borderColor: 'warning.main', position: 'relative' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#fffde7' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ConditionIcon sx={{ color: 'warning.main', fontSize: 18 }} />
            <Typography variant="caption" color="warning.main" fontWeight="bold">{data.label || 'Condição'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
            {data.key ? `${data.key} ${data.condition} ${data.value}` : 'Configure a condição...'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="success.main">SIM</Typography>
            <Typography variant="caption" color="error.main">NÃO</Typography>
        </Box>
        <Handle type="source" position={Position.Bottom} id="true" style={{ left: '25%' }} />
        <Handle type="source" position={Position.Bottom} id="false" style={{ left: '75%' }} />
    </Paper>
);

const IdentifyClientNodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 170, border: '1px solid', borderColor: '#00897b', position: 'relative', bgcolor: '#e0f2f1' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#e0f2f1' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <IdentifyClientIcon sx={{ color: '#00897b', fontSize: 18 }} />
            <Typography variant="caption" color="#00897b" fontWeight="bold">{data.label || 'Identificar Cliente'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
            Verifica se o contato tem cliente vinculado.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="success.main">Cliente</Typography>
            <Typography variant="caption" color="error.main">Não cliente</Typography>
        </Box>
        <Handle type="source" position={Position.Bottom} id="true" style={{ left: '25%' }} />
        <Handle type="source" position={Position.Bottom} id="false" style={{ left: '75%' }} />
    </Paper>
);

const IdentifyClientV2NodeComponent = ({ id, data }: any) => {
    const atendenteEnabled = data?.ativarSaidaAtendente === true;
    const nativeOptions = [
        { label: IDENTIFY_CLIENT_V2_NATIVE_OPTIONS.notClient, handle: 'false' },
        ...(atendenteEnabled ? [{ label: IDENTIFY_CLIENT_V2_NATIVE_OPTIONS.atendente, handle: 'falarAtendente' }] : []),
    ];
    const exits = [
        { label: 'Cliente', handle: 'true', color: 'success.main' },
        { label: 'Nao cliente', handle: 'false', color: 'error.main' },
        { label: 'Cliente Inativo', handle: 'inativo', color: 'warning.main' },
        { label: 'Suspenso', handle: 'suspenso', color: 'secondary.main' },
        ...(atendenteEnabled ? [{ label: 'Atendente', handle: 'falarAtendente', color: 'info.main' }] : []),
    ];
    const handleLeft = (index: number) => `${((index + 0.5) / exits.length) * 100}%`;

    return (
    <Paper sx={{ p: 1, minWidth: 230, maxWidth: 280, border: '1px solid', borderColor: '#004d40', position: 'relative', bgcolor: '#e0f2f1' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#e0f2f1' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <IdentifyClientV2Icon sx={{ color: '#004d40', fontSize: 18 }} />
            <Typography variant="caption" color="#004d40" fontWeight="bold">{data.label || 'Identificar Cliente V2'}</Typography>
        </Box>
        <Typography variant="body2" sx={{
            mt: 1,
            mb: 0.5,
            fontSize: '0.75rem',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
        }}>
            {data.askCpfMessage ? renderFormattedText(data.askCpfMessage) : IDENTIFY_CLIENT_V2_DEFAULTS.askCpfMessage}
        </Typography>
        <Stack spacing={0.5}>
            {nativeOptions.map((opt, i) => (
                <Box
                    key={opt.handle}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'background.default',
                        p: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: 22,
                    }}
                >
                    <Typography variant="caption" sx={{ flexGrow: 1, fontSize: '0.65rem' }}>
                        {numberToEmoji(i + 1)} {opt.label}
                    </Typography>
                </Box>
            ))}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {exits.map((exit) => (
                <Typography key={exit.handle} variant="caption" color={exit.color} sx={{ fontSize: '0.6rem' }}>
                    {exit.label}
                </Typography>
            ))}
        </Box>
        {exits.map((exit, index) => (
            <Handle key={exit.handle} type="source" position={Position.Bottom} id={exit.handle} style={{ left: handleLeft(index) }} />
        ))}
    </Paper>
    );
};

const ConsultarFaturasNodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 190, border: '1px solid', borderColor: '#5e35b1', position: 'relative', bgcolor: '#ede7f6' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#ede7f6' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ConsultarFaturasIcon sx={{ color: '#5e35b1', fontSize: 18 }} />
            <Typography variant="caption" color="#5e35b1" fontWeight="bold">{data.label || 'Consultar Faturas'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
            {data.quantidade || 3} fatura(s) · {data.sendMode === 'link' ? 'Link' : 'Dados'}
            {data.limitarDiasVencidas ? ` · max ${data.maxDiasVencidas || 0}d venc.` : ''}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            <Typography variant="caption" color="error.main" sx={{ fontSize: '0.6rem' }}>S/ cliente</Typography>
            <Typography variant="caption" color="success.main" sx={{ fontSize: '0.6rem' }}>OK</Typography>
            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.6rem' }}>S/ fat.</Typography>
            <Typography variant="caption" color="secondary.main" sx={{ fontSize: '0.6rem' }}>Sem solução</Typography>
        </Box>
        <Handle type="source" position={Position.Bottom} id="noClient" style={{ left: '12%' }} />
        <Handle type="source" position={Position.Bottom} id="success" style={{ left: '37%' }} />
        <Handle type="source" position={Position.Bottom} id="noInvoices" style={{ left: '62%' }} />
        <Handle type="source" position={Position.Bottom} id="sem_solucao" style={{ left: '87%' }} />
    </Paper>
);

const ConsultarFaturasV2NodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 200, border: '1px solid', borderColor: '#1565c0', position: 'relative', bgcolor: '#e3f2fd' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#e3f2fd' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ConsultarFaturasV2Icon sx={{ color: '#1565c0', fontSize: 18 }} />
            <Typography variant="caption" color="#1565c0" fontWeight="bold">{data.label || 'Consultar Faturas V2'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
            {data.quantidade || 3} fat. · multi-contrato
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            <Typography variant="caption" color="error.main" sx={{ fontSize: '0.6rem' }}>S/ cliente</Typography>
            <Typography variant="caption" color="success.main" sx={{ fontSize: '0.6rem' }}>OK</Typography>
            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.6rem' }}>S/ fat.</Typography>
            <Typography variant="caption" color="secondary.main" sx={{ fontSize: '0.6rem' }}>Sem solução</Typography>
        </Box>
        <Handle type="source" position={Position.Bottom} id="noClient" style={{ left: '12%' }} />
        <Handle type="source" position={Position.Bottom} id="success" style={{ left: '37%' }} />
        <Handle type="source" position={Position.Bottom} id="noInvoices" style={{ left: '62%' }} />
        <Handle type="source" position={Position.Bottom} id="sem_solucao" style={{ left: '87%' }} />
    </Paper>
);

const QuestionNodeComponent = ({ id, data }: any) => (
    <Paper sx={{
        p: 1,
        minWidth: 150,
        maxWidth: 250,
        border: '1px solid',
        borderColor: 'info.main',
        position: 'relative',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#e3f2fd' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <QuestionIcon sx={{ color: 'info.main', fontSize: 18 }} />
            <Typography variant="caption" color="info.main" fontWeight="bold">{data.label || 'Pergunta'}</Typography>
        </Box>
        <Typography variant="body2" sx={{
            mt: 1,
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
        }}>
            {data.text ? renderFormattedText(data.text) : 'Digite a pergunta...'}
        </Typography>
        {data.key && (
            <Chip
                label={`Salvar em: {{${data.key}}}`}
                size="small"
                color="info"
                variant="outlined"
                sx={{ mt: 1, fontSize: '0.65rem', height: 20 }}
            />
        )}
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

// Componente para Conexão com Botão de Deletar
const ClosedTicketByBotNodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 150, border: '1px solid', borderColor: 'error.main', position: 'relative', bgcolor: '#fff5f5' }}>
        <Handle type="target" position={Position.Top} />
        {id !== 'start' && (
            <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                    e.stopPropagation();
                    window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
                }}
                sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#ffebee' }, width: 20, height: 20 }}
            >
                <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <EndIcon sx={{ color: 'error.main', fontSize: 18 }} />
            <Typography variant="caption" color="error.main" fontWeight="bold">{data?.label || 'Encerrar Atendimento'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
            Encerrará o atendimento automaticamente e marcará o ticket como encerrado pelo bot.
        </Typography>
    </Paper>
);

const QueueNodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 150, border: '1px solid', borderColor: 'success.main', position: 'relative', bgcolor: '#f1f8e9' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#f1f8e9' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <QueueIcon sx={{ color: 'success.main', fontSize: 18 }} />
            <Typography variant="caption" color="success.main" fontWeight="bold">{data.label || 'Alterar Fila'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>{data.queueName ? `Destino: ${data.queueName}` : 'Selecione uma fila...'}</Typography>
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

const UserNodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 150, border: '1px solid', borderColor: '#3f51b5', position: 'relative', bgcolor: '#e8eaf6' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#e8eaf6' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <UserIcon sx={{ color: '#3f51b5', fontSize: 18 }} />
            <Typography variant="caption" color="#3f51b5" fontWeight="bold">{data.label || 'Alterar Usuário'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>{data.userName ? `Destino: ${data.userName}` : 'Selecione um usuário...'}</Typography>
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

const StopBotNodeComponent = ({ id, data }: any) => (
    <Paper sx={{
        p: 1,
        minWidth: 150,
        maxWidth: 250,
        border: '1px solid',
        borderColor: '#f44336',
        position: 'relative',
        bgcolor: '#fff5f5',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#ffebee' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StopBotIcon sx={{ color: 'error.main', fontSize: 18 }} />
            <Typography variant="caption" color="error.main" fontWeight="bold">{data.label || 'Parar Bot'}</Typography>
        </Box>
        <Typography variant="body2" sx={{
            mt: 1,
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
        }}>
            {data.text ? renderFormattedText(data.text) : 'Desativará o bot para este ticket.'}
        </Typography>
    </Paper>
);

const HttpRequestNodeComponent = ({ id, data }: any) => (
    <Paper sx={{
        p: 1,
        minWidth: 150,
        maxWidth: 250,
        border: '1px solid',
        borderColor: '#9c27b0',
        position: 'relative',
        bgcolor: '#f3e5f5',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#f3e5f5' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <HttpIcon sx={{ color: '#9c27b0', fontSize: 18 }} />
            <Typography variant="caption" color="#9c27b0" fontWeight="bold">{data.label || 'HTTP Request'}</Typography>
        </Box>
        <Typography variant="body2" sx={{
            mt: 1,
            fontSize: '0.7rem',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
        }}>
            {data.url ? data.url : 'Configure a URL...'}
        </Typography>
        {data.variableMappings && data.variableMappings.length > 0 && (
            <Chip
                label={`${data.variableMappings.length} variável(is)`}
                size="small"
                sx={{ mt: 0.5, fontSize: '0.6rem', height: 18 }}
            />
        )}
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

const IntervalNodeComponent = ({ id, data }: any) => (
    <Paper sx={{
        p: 1,
        minWidth: 150,
        maxWidth: 250,
        border: '1px solid',
        borderColor: '#ff9800',
        position: 'relative',
        bgcolor: '#fff3e0',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#fff3e0' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ScheduleIcon sx={{ color: '#ff9800', fontSize: 18 }} />
            <Typography variant="caption" color="#ff9800" fontWeight="bold">{data.label || 'Intervalo'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
            {data.seconds ? `Aguarda ${data.seconds}s` : 'Configure o intervalo...'}
        </Typography>
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

const IntegrationNodeComponent = ({ id, data }: any) => (
    <Paper sx={{ p: 1, minWidth: 150, border: '1px solid', borderColor: '#4caf50', position: 'relative', bgcolor: '#e8f5e9' }}>
        <Handle type="target" position={Position.Top} />
        <IconButton
            size="small"
            color="error"
            onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
            }}
            sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#e8f5e9' }, width: 20, height: 20 }}
        >
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <IntegrationIcon sx={{ color: '#4caf50', fontSize: 18 }} />
            <Typography variant="caption" color="#4caf50" fontWeight="bold">{data.label || 'Alterar Integração'}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>{data.integrationName ? `Destino: ${data.integrationName}` : 'Selecione uma integração...'}</Typography>
        {data.useTrigger && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: '#4caf50' }}>
                <TriggerIcon sx={{ fontSize: 14, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Gatilho Ativo</Typography>
            </Box>
        )}
        <Handle type="source" position={Position.Bottom} />
    </Paper>
);

const BusinessHoursNodeComponent = ({ id, data }: any) => {
    const sourceLabel = data.sourceType === 'connection' && data.connectionName
        ? data.connectionName
        : data.sourceType === 'queue' && data.queueName
            ? data.queueName
            : data.sourceType === 'general'
                ? 'Geral'
                : 'Não configurado';
    return (
        <Paper sx={{ p: 1, minWidth: 160, border: '1px solid', borderColor: '#607d8b', position: 'relative', bgcolor: '#eceff1' }}>
            <Handle type="target" position={Position.Top} />
            <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                    e.stopPropagation();
                    window.dispatchEvent(new CustomEvent('deleteNode', { detail: { id } }));
                }}
                sx={{ position: 'absolute', right: -10, top: -10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.main', '&:hover': { bgcolor: '#eceff1' }, width: 20, height: 20 }}
            >
                <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTimeIcon sx={{ color: '#607d8b', fontSize: 18 }} />
                <Typography variant="caption" color="#607d8b" fontWeight="bold">{data.label || 'Horário Expediente'}</Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
                {sourceLabel}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, fontSize: '0.7rem' }}>
                <Typography variant="caption" color="success.main">Dentro</Typography>
                <Typography variant="caption" color="error.main">Fora</Typography>
            </Box>
            <Handle type="source" position={Position.Bottom} id="inside" style={{ left: '25%' }} />
            <Handle type="source" position={Position.Bottom} id="outside" style={{ left: '75%' }} />
        </Paper>
    );
};

const ButtonEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
}: EdgeProps) => {
    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    // Acessar a função de deletar via evento de customização
    const onEdgeClick = (evt: any) => {
        evt.stopPropagation();
        const event = new CustomEvent('deleteEdge', { detail: { id } });
        window.dispatchEvent(event);
    };

    return (
        <>
            <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
            <EdgeLabelRenderer>
                <div
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                        fontSize: 12,
                        pointerEvents: 'all',
                    }}
                    className="nodrag nopan"
                >
                    <button
                        style={{
                            width: 20,
                            height: 20,
                            background: '#d32f2f',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                        onClick={onEdgeClick}
                        title="Remover Conexão"
                    >
                        ×
                    </button>
                </div>
            </EdgeLabelRenderer>
        </>
    );
};

const nodeTypes = {
    start: StartNodeComponent,
    message: MessageNodeComponent,
    menu: MenuNodeComponent,
    condition: ConditionNodeComponent,
    identifyClient: IdentifyClientNodeComponent,
    identifyClientV2: IdentifyClientV2NodeComponent,
    consultarFaturas: ConsultarFaturasNodeComponent,
    consultarFaturasV2: ConsultarFaturasV2NodeComponent,
    question: QuestionNodeComponent,
    changeQueue: QueueNodeComponent,
    changeUser: UserNodeComponent,
    stopBot: StopBotNodeComponent,
    closedTicketByBot: ClosedTicketByBotNodeComponent,
    httpRequest: HttpRequestNodeComponent,
    interval: IntervalNodeComponent,
    changeIntegration: IntegrationNodeComponent,
    singleBlock: SingleBlockNodeComponent,
    businessHours: BusinessHoursNodeComponent,
};

const edgeTypes = {
    button: ButtonEdge,
};

const initialNodes: Node[] = [
    {
        id: 'start',
        type: 'start',
        data: { label: 'Ponto de partida' },
        position: { x: 250, y: 5 },
        deletable: false,
    },
];

const initialEdges: Edge[] = [];

const parseFlowData = (rawFlowData: any) => {
    if (!rawFlowData) return { nodes: [], connections: [] };
    if (typeof rawFlowData === 'string') {
        try {
            return JSON.parse(rawFlowData);
        } catch (error) {
            return { nodes: [], connections: [] };
        }
    }
    return rawFlowData;
};

const normalizeFlowNode = (node: any): Node => {
    if (node.id === 'start') {
        return {
            ...node,
            type: 'start',
            deletable: false,
            data: { label: 'Ponto de partida', ...(node.data || {}) },
        };
    }

    const data = { ...(node.data || {}) };
    if (node.type && data.type === undefined) data.type = node.type;
    return { ...node, data };
};

const normalizeFlowEdge = (edge: any): Edge => ({
    ...edge,
    id: edge.id || `e${edge.source}-${edge.target}`,
    type: 'button',
});

const FlowEditorInner = () => {
    const { flowId } = useParams();
    const navigate = useNavigate();
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const [loading, setLoading] = useState(true);
    const [flowName, setFlowName] = useState('');
    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [simulatorOpen, setSimulatorOpen] = useState(false);
    const [queues, setQueues] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [testResult, setTestResult] = useState<any>(null);
    const [testError, setTestError] = useState<string | null>(null);
    const [integrations, setIntegrations] = useState<any[]>([]);
    const [connections, setConnections] = useState<any[]>([]);
    const [company, setCompany] = useState<any>(null);
    const [singleBlockModalOpen, setSingleBlockModalOpen] = useState(false);
    const [consultarFaturasModalOpen, setConsultarFaturasModalOpen] = useState(false);
    const [consultarFaturasV2ModalOpen, setConsultarFaturasV2ModalOpen] = useState(false);
    const [identifyClientV2ModalOpen, setIdentifyClientV2ModalOpen] = useState(false);
    const [nodePaletteOpen, setNodePaletteOpen] = useState(true);
    const [editingOutOfHoursNodeId, setEditingOutOfHoursNodeId] = useState<string | null>(null);
    const importFileInputRef = useRef<HTMLInputElement>(null);

    const nodeMenuActions = [
        { icon: <MessageIcon />, name: 'Mensagem', type: 'message' },
        { icon: <ContentIcon />, name: 'Conteúdo (Bloco)', type: 'singleBlock' },
        { icon: <MenuIcon />, name: 'Menu', type: 'menu' },
        { icon: <ConditionIcon />, name: 'Condição', type: 'condition' },
        { icon: <IdentifyClientIcon />, name: 'Identificar Cliente', type: 'identifyClient' },
        { icon: <IdentifyClientV2Icon />, name: 'Identificar Cliente V2', type: 'identifyClientV2' },
        { icon: <ConsultarFaturasIcon />, name: 'Consultar Faturas', type: 'consultarFaturas' },
        { icon: <ConsultarFaturasV2Icon />, name: 'Consultar Faturas V2', type: 'consultarFaturasV2' },
        { icon: <QuestionIcon />, name: 'Pergunta', type: 'question' },
        { icon: <QueueIcon />, name: 'Alterar Fila', type: 'changeQueue' },
        { icon: <UserIcon />, name: 'Alterar Usuário', type: 'changeUser' },
        { icon: <StopBotIcon />, name: 'Parar Bot', type: 'stopBot' },
        { icon: <EndIcon />, name: 'Encerrar atendimento', type: 'closedTicketByBot' },
        { icon: <HttpIcon />, name: 'HTTP Request', type: 'httpRequest' },
        { icon: <ScheduleIcon />, name: 'Intervalo', type: 'interval' },
        { icon: <AccessTimeIcon />, name: 'Horário Expediente', type: 'businessHours' },
    ];

    useEffect(() => {
        const fetchQueues = async () => {
            try {
                const response = await api.get('/atendimento-setores') as any;
                const data = Array.isArray(response) ? response : (response.data || []);
                // Garantir que IDs sejam strings para consistência no Select
                const normalized = (data || []).map((q: any) => ({
                    ...q,
                    id: String(q.id),
                    name: q.name || q.nome,
                    businessHoursEnabled: q.businessHoursEnabled ?? q.expediente_ativo ?? true,
                    outOfHoursMessageText: q.outOfHoursMessageText || q.texto_fora_expediente || '',
                }));
                setQueues(normalized);
            } catch (err) {
                // Erro ao buscar filas
            }
        };
        const fetchUsers = async () => {
            try {
                const response = await api.get('/users') as any;
                const data = Array.isArray(response) ? response : (response.data || []);
                const normalized = (data || []).map((u: any) => ({ ...u, id: String(u.id), name: u.name || u.nome || u.usuario }));
                setUsers(normalized);
            } catch (err) {
                // Erro ao buscar usuários
            }
        };
        const fetchIntegrations = async () => {
            try {
                setIntegrations([]);
            } catch (err) {
                // Erro ao buscar integrações
            }
        };
        const fetchConnections = async () => {
            try {
                const response = await api.get('/atendimento/conexoes') as any;
                const data = Array.isArray(response) ? response : (response.data || []);
                const normalized = (data || []).map((c: any) => ({
                    ...c,
                    id: String(c.id),
                    name: c.name || c.nome,
                    businessHoursEnabled: c.businessHoursEnabled ?? c.expediente_ativo ?? true,
                    outOfHoursMessageText: c.outOfHoursMessageText || c.texto_fora_expediente || '',
                }));
                setConnections(normalized);
            } catch (err) {
                // Erro ao buscar conexões
            }
        };
        const fetchCompany = async () => {
            try {
                const data = await api.get('/atendimento/configuracoes') as any;
                setCompany({
                    businessHoursEnabled: data?.expediente_ativo || false,
                    outOfHoursMessageText: data?.texto_fora_expediente || '',
                });
            } catch (err) {
                // Erro ao buscar empresa
            }
        };
        fetchQueues();
        fetchUsers();
        fetchIntegrations();
        fetchConnections();
        fetchCompany();
    }, []);

    useEffect(() => {
        setEditingOutOfHoursNodeId(null);
    }, [selectedNode?.id]);

    const onConnect = useCallback(
        (params: Connection | Edge) => {
            // Verificar se já existe uma conexão de saída do mesmo handle
            // Nodes 'condition' e 'menu' podem ter múltiplas saídas, mas cada handle só pode ter uma conexão
            const existingOutgoingEdge = edges.find(
                (edge) => edge.source === params.source &&
                    (edge.sourceHandle === params.sourceHandle ||
                        (!edge.sourceHandle && !params.sourceHandle))
            );

            if (existingOutgoingEdge) {
                toast.warning('Este node já possui uma conexão de saída neste ponto. Remova a conexão existente antes de criar uma nova.');
                return;
            }

            setEdges((eds) => addEdge({ ...params, type: 'button' }, eds));
        },
        [setEdges, edges]
    );

    const onNodeClick = useCallback((_: any, node: Node) => {
        if (node.id === 'start' || node.type === 'start') return;
        if (node.type === 'singleBlock') {
            setSelectedNode(node);
            setSingleBlockModalOpen(true);
        } else if (node.type === 'consultarFaturas' || node.data?.type === 'consultarFaturas') {
            setSelectedNode(node);
            setConsultarFaturasModalOpen(true);
        } else if (node.type === 'consultarFaturasV2' || node.data?.type === 'consultarFaturasV2') {
            setSelectedNode(node);
            setConsultarFaturasV2ModalOpen(true);
        } else if (node.type === 'identifyClientV2' || node.data?.type === 'identifyClientV2') {
            setSelectedNode(node);
            setIdentifyClientV2ModalOpen(true);
        } else {
            setSelectedNode(node);
        }
    }, []);

    const onPaneClick = useCallback(() => {
        setSelectedNode(null);
        setConsultarFaturasModalOpen(false);
        setConsultarFaturasV2ModalOpen(false);
        setIdentifyClientV2ModalOpen(false);
    }, []);

    const updateNodeData = useCallback((nodeId: string, newData: any) => {
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === nodeId) {
                    return { ...node, data: { ...node.data, ...newData } };
                }
                return node;
            })
        );
        setSelectedNode((prev) => (prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...newData } } : prev));
    }, [setNodes]);

    const deleteNode = useCallback((nodeId: string) => {
        if (nodeId === 'start') {
            toast.warning('O nó de início não pode ser removido.');
            return;
        }
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
        setSelectedNode(null);
    }, [setNodes, setEdges]);

    const deleteEdge = useCallback((edgeId: string) => {
        setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
    }, [setEdges]);

    const copyNode = useCallback((nodeId: string) => {
        const nodeToCopy = nodes.find(n => n.id === nodeId);
        if (!nodeToCopy) return;

        const newId = `node_${Math.random().toString(36).substr(2, 9)}`;
        const newNode: Node = {
            ...nodeToCopy,
            id: newId,
            position: {
                x: nodeToCopy.position.x + 50,
                y: nodeToCopy.position.y + 50,
            },
            data: {
                ...nodeToCopy.data,
            },
        };

        setNodes((nds) => nds.concat(newNode));
        setSelectedNode(newNode);
        toast.success('Node copiado com sucesso!');
    }, [nodes, setNodes]);

    const exportNodes = useCallback(() => {
        const payload = { nodes, edges, exportedAt: new Date().toISOString() };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `flow-nodes-${flowName || flowId || 'export'}-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Nós exportados com sucesso!');
    }, [nodes, edges, flowName, flowId]);

    const importNodes = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                const parsed = JSON.parse(text) as { nodes?: Node[]; edges?: Edge[] };
                const importedNodes = Array.isArray(parsed.nodes) ? parsed.nodes : [];
                const importedEdges = Array.isArray(parsed.edges) ? parsed.edges : [];
                const offset = { x: 280, y: 120 };
                const idMap: Record<string, string> = {};
                const newNodes: Node[] = importedNodes
                    .filter((n) => n.id !== 'start' && n.type !== 'start')
                    .map((node) => {
                        const newId = `node_${Math.random().toString(36).substr(2, 9)}`;
                        idMap[node.id] = newId;
                        return {
                            ...node,
                            id: newId,
                            position: {
                                x: (node.position?.x ?? 0) + offset.x,
                                y: (node.position?.y ?? 0) + offset.y,
                            },
                        };
                    });
                const newEdges: Edge[] = importedEdges
                    .filter((e) => idMap[e.source] && idMap[e.target])
                    .map((edge) => ({
                        ...edge,
                        id: `e${Math.random().toString(36).substr(2, 9)}`,
                        source: idMap[edge.source],
                        target: idMap[edge.target],
                    }));
                setNodes((nds) => nds.concat(newNodes));
                setEdges((eds) => eds.concat(newEdges));
                toast.success(`${newNodes.length} nó(s) e ${newEdges.length} conexão(ões) importados.`);
            } catch (err) {
                toast.error('Arquivo inválido. Use um JSON exportado pelo Flow Builder.');
            }
            event.target.value = '';
        };
        reader.readAsText(file);
    }, [setNodes, setEdges]);

    useEffect(() => {
        const handler = (event: any) => {
            if (event.detail?.id) {
                deleteEdge(event.detail.id);
            }
        };
        const nodeHandler = (event: any) => {
            if (event.detail?.id) {
                deleteNode(event.detail.id);
            }
        };
        window.addEventListener('deleteEdge', handler);
        window.addEventListener('deleteNode', nodeHandler);
        return () => {
            window.removeEventListener('deleteEdge', handler);
            window.removeEventListener('deleteNode', nodeHandler);
        };
    }, [deleteEdge, deleteNode]);

    useEffect(() => {
        const fetchFlow = async () => {
            try {
                const response = await api.get(`/atendimento/fluxos/${flowId}`) as any;
                const flow = response?.flow || response?.data?.flow || response?.data || response;
                const flowData = parseFlowData(flow?.flowData ?? flow?.flow_data);
                const savedNodes = Array.isArray(flowData?.nodes) ? flowData.nodes : [];
                const savedEdges = Array.isArray(flowData?.connections)
                    ? flowData.connections
                    : (Array.isArray(flowData?.edges) ? flowData.edges : []);

                setFlowName(flow?.name || flow?.nome || '');
                if (savedNodes.length > 0) {
                    const normalizedNodes = savedNodes.map(normalizeFlowNode);
                    const hasStartNode = normalizedNodes.some((node: any) => node.id === 'start' || node.type === 'start');

                    setNodes(hasStartNode ? normalizedNodes : [...initialNodes, ...normalizedNodes]);
                    setEdges(savedEdges.map(normalizeFlowEdge));
                } else {
                    setNodes(initialNodes);
                    setEdges(initialEdges);
                }
            } catch (error) {
                toast.error('Erro ao carregar fluxo');
                navigate('/settings/parameters');
            } finally {
                setLoading(false);
            }
        };
        fetchFlow();
    }, [flowId, navigate, setNodes, setEdges]);

    const onSave = async () => {
        try {
            await api.put(`/atendimento/fluxos/${flowId}`, {
                flowData: {
                    nodes,
                    connections: edges,
                },
            });
            toast.success('Fluxo salvo com sucesso!');
        } catch (error) {
            toast.error('Erro ao salvar fluxo');
        }
    };

    const addNode = (type: string, position?: { x: number; y: number }) => {
        const id = `node_${Math.random().toString(36).substr(2, 9)}`;
        const newNode: Node = {
            id,
            type,
            position: position || {
                x: Math.random() * 400,
                y: Math.random() * 400,
            },
            // Personalizar dados baseado no tipo
            data: {
                label: type === 'message' ? 'Mensagem' :
                    type === 'menu' ? 'Menu' :
                        type === 'condition' ? 'Condição' :
                            type === 'identifyClient' ? 'Identificar Cliente' :
                            type === 'identifyClientV2' ? 'Identificar Cliente V2' :
                                type === 'consultarFaturas' ? 'Consultar Faturas' :
                                type === 'consultarFaturasV2' ? 'Consultar Faturas V2' :
                                type === 'question' ? 'Pergunta' :
                                type === 'changeQueue' ? 'Alterar Fila' :
                                    type === 'changeUser' ? 'Alterar Usuário' :
                                        type === 'stopBot' ? 'Parar Bot' :
                                            type === 'httpRequest' ? 'HTTP Request' :
                                                type === 'interval' ? 'Intervalo' :
                                                    type === 'changeIntegration' ? 'Alterar Integração' :
                                                        type === 'singleBlock' ? 'Conteúdo' :
                                                            type === 'businessHours' ? 'Horário Expediente' :
                                                                type === 'closedTicketByBot' ? 'Encerrar atendimento' :
                type,
                type,
                ...(type === 'message' && { text: '' }),
                ...(type === 'menu' && { message: '', options: [] }),
                ...(type === 'condition' && { key: '', condition: 'equals', value: '' }),
                ...(type === 'identifyClientV2' && {
                    label: 'Identificar Cliente V2',
                    ...IDENTIFY_CLIENT_V2_DEFAULTS,
                }),
                ...(type === 'consultarFaturas' && {
                    label: 'Consultar Faturas',
                    ...CONSULTAR_FATURAS_DEFAULTS,
                }),
                ...(type === 'consultarFaturasV2' && {
                    label: 'Consultar Faturas V2',
                    ...CONSULTAR_FATURAS_V2_DEFAULTS,
                }),
                ...(type === 'question' && { text: '', key: '' }),
                ...(type === 'changeQueue' && { queueId: '', queueName: '', sendTransferMessage: true }),
                ...(type === 'changeUser' && { userId: '', userName: '' }),
                ...(type === 'changeIntegration' && { integrationId: '', integrationName: '', useTrigger: false, triggerMessage: '' }),
                ...(type === 'stopBot' && { text: '🤖 O atendimento automático foi encerrado. Aguarde um atendente.' }), 
                ...(type === 'httpRequest' && { url: '', method: 'GET', headers: [], body: '', variableMappings: [] }),
                ...(type === 'interval' && { seconds: 1 }),
                ...(type === 'singleBlock' && { elements: [], seq: [] }),
                ...(type === 'businessHours' && { sourceType: 'general', connectionId: '', connectionName: '', queueId: '', queueName: '', sendMessageOutOfHours: false, outOfHoursMessage: '' }),
                ...(type === 'closedTicketByBot' && { text: 'Agradecemos pelo seu contato. Precisando de algo, estaremos aqui para ajudar.' }),
            },
        };
        setNodes((nds) => nds.concat(newNode));
    };


    const onDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const onDrop = (event: React.DragEvent) => {
        event.preventDefault();

        const nodeType = event.dataTransfer.getData('application/reactflow');

        if (!nodeType || !reactFlowInstance) {
            return;
        }

        const position = reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        });

        addNode(nodeType, position);
    };

    const testHttpRequest = async () => {
        if (!selectedNode || selectedNode.data.type !== 'httpRequest') {
            return;
        }

        const { url, method, headers, body } = selectedNode.data;

        if (!url) {
            toast.error('URL é obrigatória para testar a requisição');
            return;
        }

        setTestLoading(true);
        setTestError(null);
        setTestResult(null);
        setTestDialogOpen(true);

        try {
            const requestHeaders: Record<string, string> = {
                'Content-Type': 'application/json',
            };

            // Adicionar headers customizados
            (headers || []).forEach((header: any) => {
                if (header.key && header.value) {
                    requestHeaders[header.key] = header.value;
                }
            });

            // Preparar body se houver
            let requestBody: any = undefined;
            if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
                try {
                    requestBody = JSON.parse(body);
                } catch (e) {
                    setTestError('Body JSON inválido');
                    setTestLoading(false);
                    return;
                }
            }

            const startTime = Date.now();
            const response = await fetch(url, {
                method: (method || 'GET').toUpperCase(),
                headers: requestHeaders,
                body: requestBody ? JSON.stringify(requestBody) : undefined,
            });

            const endTime = Date.now();
            const responseTime = endTime - startTime;

            let responseData: any;
            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                responseData = await response.json();
            } else {
                responseData = await response.text();
            }

            setTestResult({
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                data: responseData,
                responseTime,
            });
        } catch (error: any) {
            setTestError(error.message || 'Erro ao executar requisição HTTP');
        } finally {
            setTestLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: 'calc(100vh - 130px)', width: '100%', position: 'relative' }}>
            <AppBar position="static" color="default" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Toolbar variant="dense">
                    <IconButton edge="start" color="inherit" onClick={() => navigate('/settings/parameters')} sx={{ mr: 2 }}>
                        <BackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Editando: {flowName}
                    </Typography>
                    <Button startIcon={<FileDownloadIcon />} variant="outlined" onClick={exportNodes} size="small" sx={{ mr: 1 }}>
                        Exportar nós
                    </Button>
                    <Button
                        startIcon={<FileUploadIcon />}
                        variant="outlined"
                        onClick={() => importFileInputRef.current?.click()}
                        size="small"
                        sx={{ mr: 1 }}
                    >
                        Importar nós
                    </Button>
                    <input
                        ref={importFileInputRef}
                        type="file"
                        accept=".json,application/json"
                        style={{ display: 'none' }}
                        onChange={importNodes}
                    />
                    <Button startIcon={<PlayIcon />} variant="outlined" onClick={() => setSimulatorOpen(true)} size="small" sx={{ mr: 1 }}>
                        Testar Flow
                    </Button>
                    <Button startIcon={<SaveIcon />} variant="contained" onClick={onSave} size="small">
                        Salvar
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ display: 'flex', height: '100%', position: 'relative' }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: nodePaletteOpen ? FLOW_PALETTE_WIDTH : FLOW_PALETTE_COLLAPSED_WIDTH,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        transition: 'width 0.2s ease',
                        overflow: 'hidden',
                        zIndex: 20,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: nodePaletteOpen ? 'space-between' : 'center',
                            px: nodePaletteOpen ? 1.5 : 0.5,
                            py: 1,
                            minHeight: 48,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {nodePaletteOpen && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <WidgetsIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                    Componentes
                                </Typography>
                            </Box>
                        )}
                        <Tooltip title={nodePaletteOpen ? 'Recolher painel' : 'Expandir painel'} placement="right">
                            <IconButton size="small" onClick={() => setNodePaletteOpen((prev) => !prev)}>
                                {nodePaletteOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {nodePaletteOpen ? (
                        <>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                                Arraste para a tela ou clique para adicionar
                            </Typography>
                            <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
                                <Stack spacing={0.75}>
                                    {nodeMenuActions.map((action) => (
                                        <Box
                                            key={action.type}
                                            draggable
                                            onDragStart={(e) => onPaletteDragStart(e, action.type)}
                                            onClick={() => addNode(action.type)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.25,
                                                px: 1.25,
                                                py: 1,
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'background.default',
                                                cursor: 'grab',
                                                userSelect: 'none',
                                                transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                    borderColor: 'primary.light',
                                                },
                                                '&:active': {
                                                    cursor: 'grabbing',
                                                    boxShadow: 1,
                                                },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', color: 'primary.main', flexShrink: 0 }}>
                                                {action.icon}
                                            </Box>
                                            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                                                {action.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
                            <Stack spacing={0.5} alignItems="center">
                                {nodeMenuActions.map((action) => (
                                    <Tooltip key={action.type} title={action.name} placement="right">
                                        <IconButton
                                            size="small"
                                            draggable
                                            onDragStart={(e) => onPaletteDragStart(e, action.type)}
                                            onClick={() => addNode(action.type)}
                                            sx={{
                                                cursor: 'grab',
                                                color: 'primary.main',
                                                '&:active': { cursor: 'grabbing' },
                                            }}
                                        >
                                            {action.icon}
                                        </IconButton>
                                    </Tooltip>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Paper>

                <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', position: 'relative', minWidth: 0, ...flowEditorHandleSx }}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        onPaneClick={onPaneClick}
                        onInit={setReactFlowInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        deleteKeyCode="Delete"
                        fitView
                    >
                        <Background />
                        <Controls />
                        <MiniMap />
                        <Panel position="top-right">
                            <Paper sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.8)' }}>
                                <Typography variant="caption">Clique em um nó para editar</Typography>
                            </Paper>
                        </Panel>
                    </ReactFlow>

                    {/* Painel de Propriedades */}
                    {selectedNode && selectedNode.data?.type !== 'consultarFaturas' && selectedNode.data?.type !== 'consultarFaturasV2' && selectedNode.data?.type !== 'identifyClientV2' && (
                        <Paper
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: 300,
                                borderRadius: 0,
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                zIndex: 100,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="h6">Propriedades</Typography>
                                <IconButton size="small" onClick={() => setSelectedNode(null)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <Divider />
                            <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="Nome do Commponente"
                                        fullWidth
                                        value={selectedNode.data.label || ''}
                                        onChange={(e) => updateNodeData(selectedNode.id, { label: e.target.value })}
                                        size="small"
                                    />

                                    {selectedNode.type === 'start' && (
                                        <Alert severity="info">
                                            Este é o ponto de partida do fluxo. Não possui conexões de entrada — apenas uma saída para o próximo nó.
                                        </Alert>
                                    )}

                                    {selectedNode.data.type === 'message' && (
                                        <SysEditorWhatsapp
                                            value={selectedNode.data.text || ''}
                                            onChange={(value) => updateNodeData(selectedNode.id, { text: value })}
                                            placeholder="Digite a mensagem que será enviada..."
                                            label="Mensagem"
                                            rows={4}
                                            maxLength={1000}
                                            showPreview={true}
                                            showVariables={true}
                                            variables={FLOW_BOT_VARIABLES}
                                        />
                                    )}

                                    {selectedNode.data.type === 'menu' && (
                                        <>
                                            <SysEditorWhatsapp
                                                value={selectedNode.data.message || ''}
                                                onChange={(value) => updateNodeData(selectedNode.id, { message: value })}
                                                placeholder="Digite a mensagem do menu..."
                                                label="Mensagem do Menu"
                                                rows={3}
                                                maxLength={1000}
                                                showPreview={true}
                                                showVariables={true}
                                                variables={FLOW_BOT_VARIABLES}
                                            />
                                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Opções</Typography>
                                            <Stack spacing={1}>
                                                {(selectedNode.data.options || []).map((opt: any, index: number) => (
                                                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center' }}>
                                                            {numberToEmoji(index + 1)}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            value={opt.label || ''}
                                                            onChange={(e) => {
                                                                const newOpts = [...selectedNode.data.options];
                                                                newOpts[index] = { ...newOpts[index], label: e.target.value };
                                                                updateNodeData(selectedNode.id, { options: newOpts });
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const newOpts = selectedNode.data.options.filter((_: any, i: number) => i !== index);
                                                                updateNodeData(selectedNode.id, { options: newOpts });
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Button
                                                    startIcon={<AddCircleIcon />}
                                                    onClick={() => {
                                                        const newOpts = [...(selectedNode.data.options || []), { label: 'Nova Opção' }];
                                                        updateNodeData(selectedNode.id, { options: newOpts });
                                                    }}
                                                >
                                                    Adicionar Opção
                                                </Button>
                                            </Stack>
                                        </>
                                    )}

                                    {selectedNode.data.type === 'condition' && (
                                        <>
                                            <TextField
                                                label="Chave da Variável"
                                                fullWidth
                                                value={selectedNode.data.key || ''}
                                                onChange={(e) => updateNodeData(selectedNode.id, { key: e.target.value })}
                                                size="small"
                                            />
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Condição</InputLabel>
                                                <Select
                                                    value={selectedNode.data.condition || 'equals'}
                                                    label="Condição"
                                                    onChange={(e) => updateNodeData(selectedNode.id, { condition: e.target.value })}
                                                >
                                                    <MenuItem value="equals">Igual a</MenuItem>
                                                    <MenuItem value="not_equals">Diferente de</MenuItem>
                                                    <MenuItem value="contains">Contém</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                label="Valor"
                                                fullWidth
                                                value={selectedNode.data.value || ''}
                                                onChange={(e) => updateNodeData(selectedNode.id, { value: e.target.value })}
                                                size="small"
                                            />
                                        </>
                                    )}

                                    {selectedNode.data.type === 'identifyClient' && (
                                        <Alert severity="info">
                                            Este node verifica se o contato do ticket esta vinculado a um cliente. Use a saida Cliente para true e Nao cliente para false.
                                        </Alert>
                                    )}

                                    {selectedNode.data.type === 'question' && (
                                        <>
                                            <SysEditorWhatsapp
                                                value={selectedNode.data.text || ''}
                                                onChange={(value) => updateNodeData(selectedNode.id, { text: value })}
                                                placeholder="Digite a pergunta que será enviada ao cliente..."
                                                label="Pergunta ao Cliente"
                                                rows={4}
                                                maxLength={1000}
                                                showPreview={true}
                                                showVariables={true}
                                                variables={FLOW_BOT_VARIABLES}
                                            />
                                            <TextField
                                                label="Nome da Variável (para salvar)"
                                                fullWidth
                                                value={selectedNode.data.key || ''}
                                                placeholder="Ex: nome_cliente"
                                                onChange={(e) => updateNodeData(selectedNode.id, { key: e.target.value })}
                                                helperText="Use {{nome_da_variavel}} em outros nós para exibir a resposta."
                                                size="small"
                                            />
                                        </>
                                    )}

                                    {selectedNode.data.type === 'changeQueue' && (
                                        <Stack spacing={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Selecionar Fila</InputLabel>
                                                <Select
                                                    value={selectedNode.data.queueId || ''}
                                                    label="Selecionar Fila"
                                                    onChange={(e) => {
                                                        const q = queues.find(que => que.id === String(e.target.value));
                                                        updateNodeData(selectedNode.id, {
                                                            queueId: String(e.target.value),
                                                            queueName: q?.name || ''
                                                        });
                                                    }}
                                                >
                                                    {queues.length === 0 && <MenuItem disabled value=""><em>Nenhuma fila carregada</em></MenuItem>}
                                                    {queues.map((q) => (
                                                        <MenuItem key={q.id} value={String(q.id)}>{q.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedNode.data.sendTransferMessage !== false}
                                                        onChange={(e) => updateNodeData(selectedNode.id, { sendTransferMessage: e.target.checked })}
                                                    />
                                                }
                                                label={<Typography variant="body2">Enviar mensagem de transferência</Typography>}
                                            />
                                        </Stack>
                                    )}

                                    {selectedNode.data.type === 'changeUser' && (
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Selecionar Usuário</InputLabel>
                                            <Select
                                                value={selectedNode.data.userId || ''}
                                                label="Selecionar Usuário"
                                                onChange={(e) => {
                                                    const u = users.find(user => user.id === String(e.target.value));
                                                    updateNodeData(selectedNode.id, {
                                                        userId: String(e.target.value),
                                                        userName: u?.name || ''
                                                    });
                                                }}
                                            >
                                                {users.length === 0 && <MenuItem disabled value=""><em>Nenhum usuário carregado</em></MenuItem>}
                                                {users.map((u) => (
                                                    <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {selectedNode.data.type === 'changeIntegration' && (
                                        <Stack spacing={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Selecionar Integração</InputLabel>
                                                <Select
                                                    value={selectedNode.data.integrationId || ''}
                                                    label="Selecionar Integração"
                                                    onChange={(e) => {
                                                        const i = integrations.find(integ => integ.id === e.target.value);
                                                        updateNodeData(selectedNode.id, {
                                                            integrationId: e.target.value,
                                                            integrationName: i?.name || ''
                                                        });
                                                    }}
                                                >
                                                    {integrations.length === 0 && <MenuItem disabled value=""><em>Nenhuma integração carregada</em></MenuItem>}
                                                    {integrations.map((i) => (
                                                        <MenuItem key={i.id} value={i.id}>{i.name} ({i.type})</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedNode.data.useTrigger || false}
                                                        onChange={(e) => updateNodeData(selectedNode.id, { useTrigger: e.target.checked })}
                                                    />
                                                }
                                                label={<Typography variant="body2">Ativação Imediata (Gatilho)</Typography>}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                Inicia a nova integração imediatamente sem esperar nova mensagem do usuário.
                                            </Typography>

                                            {selectedNode.data.useTrigger && (
                                                <TextField
                                                    label="Mensagem de Gatilho (Opcional)"
                                                    fullWidth
                                                    multiline
                                                    rows={8}
                                                    value={selectedNode.data.triggerMessage || ''}
                                                    onChange={(e) => updateNodeData(selectedNode.id, { triggerMessage: e.target.value })}
                                                    placeholder="Digite a mensagem para acionar o bot, exemplo: Cliente quer dados para pagamento de sua mensalidade..."
                                                    size="small"
                                                    helperText="Se vazio, envia uma string vazia."
                                                />
                                            )}
                                        </Stack>
                                    )}

                                    {selectedNode.data.type === 'businessHours' && (
                                        <Stack spacing={2}>
                                            {(() => {
                                                const sourceType = selectedNode.data.sourceType || 'general';
                                                const conn = sourceType === 'connection' && selectedNode.data.connectionId
                                                    ? connections.find((c: any) => c.id === String(selectedNode.data.connectionId))
                                                    : null;
                                                const queue = sourceType === 'queue' && selectedNode.data.queueId
                                                    ? queues.find((q: any) => q.id === String(selectedNode.data.queueId))
                                                    : null;
                                                const expedienteDesativado = sourceType === 'connection' && conn
                                                    ? !conn.businessHoursEnabled
                                                    : sourceType === 'queue' && queue
                                                        ? !queue.businessHoursEnabled
                                                        : sourceType === 'general' && company
                                                            ? !company.businessHoursEnabled
                                                            : false;
                                                return expedienteDesativado ? (
                                                    <Alert severity="warning" sx={{ mb: 0 }}>
                                                        O expediente da opção escolhida está desativado. O nó sempre considerará &quot;dentro do expediente&quot;. Ative o horário de expediente na conexão, na fila ou nas configurações gerais para que a verificação funcione.
                                                    </Alert>
                                                ) : null;
                                            })()}
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Verificar horário por</InputLabel>
                                                <Select
                                                    value={selectedNode.data.sourceType || 'general'}
                                                    label="Verificar horário por"
                                                    onChange={(e) => {
                                                        const sourceType = e.target.value as 'connection' | 'queue' | 'general';
                                                        updateNodeData(selectedNode.id, {
                                                            sourceType,
                                                            ...(sourceType !== 'connection' && { connectionId: '', connectionName: '' }),
                                                            ...(sourceType !== 'queue' && { queueId: '', queueName: '' }),
                                                        });
                                                    }}
                                                >
                                                    <MenuItem value="connection">Por conexão</MenuItem>
                                                    <MenuItem value="queue">Por fila</MenuItem>
                                                    <MenuItem value="general">Configuração geral</MenuItem>
                                                </Select>
                                            </FormControl>

                                            {selectedNode.data.sourceType === 'connection' && (
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Selecionar conexão</InputLabel>
                                                    <Select
                                                        value={selectedNode.data.connectionId || ''}
                                                        label="Selecionar conexão"
                                                        onChange={(e) => {
                                                            const c = connections.find(conn => conn.id === String(e.target.value));
                                                            updateNodeData(selectedNode.id, {
                                                                connectionId: String(e.target.value),
                                                                connectionName: c?.name || ''
                                                            });
                                                        }}
                                                    >
                                                        {connections.length === 0 && <MenuItem disabled value=""><em>Nenhuma conexão carregada</em></MenuItem>}
                                                        {connections.map((c) => (
                                                            <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}

                                            {selectedNode.data.sourceType === 'queue' && (
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Selecionar fila</InputLabel>
                                                    <Select
                                                        value={selectedNode.data.queueId || ''}
                                                        label="Selecionar fila"
                                                        onChange={(e) => {
                                                            const q = queues.find(que => que.id === String(e.target.value));
                                                            updateNodeData(selectedNode.id, {
                                                                queueId: String(e.target.value),
                                                                queueName: q?.name || ''
                                                            });
                                                        }}
                                                    >
                                                        {queues.length === 0 && <MenuItem disabled value=""><em>Nenhuma fila carregada</em></MenuItem>}
                                                        {queues.map((q) => (
                                                            <MenuItem key={q.id} value={String(q.id)}>{q.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}

                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedNode.data.sendMessageOutOfHours || false}
                                                        onChange={(e) => updateNodeData(selectedNode.id, { sendMessageOutOfHours: e.target.checked })}
                                                    />
                                                }
                                                label={<Typography variant="body2">Enviar mensagem fora do horário</Typography>}
                                            />

                                            {selectedNode.data.sendMessageOutOfHours && (() => {
                                                const st = selectedNode.data.sourceType || 'general';
                                                const conn = st === 'connection' && selectedNode.data.connectionId
                                                    ? connections.find((c: any) => c.id === String(selectedNode.data.connectionId))
                                                    : null;
                                                const queue = st === 'queue' && selectedNode.data.queueId
                                                    ? queues.find((q: any) => q.id === String(selectedNode.data.queueId))
                                                    : null;
                                                const sourceMessage = st === 'connection' && conn
                                                    ? (conn.outOfHoursMessageText || '')
                                                    : st === 'queue' && queue
                                                        ? (queue.outOfHoursMessageText || '')
                                                        : st === 'general' && company
                                                            ? (company.outOfHoursMessageText || '')
                                                            : '';
                                                const displayMessage = (selectedNode.data.outOfHoursMessage ?? sourceMessage) || '';
                                                const isEditing = editingOutOfHoursNodeId === selectedNode.id;
                                                return isEditing ? (
                                                    <Stack spacing={1}>
                                                        <SysEditorWhatsapp
                                                            value={selectedNode.data.outOfHoursMessage ?? sourceMessage}
                                                            onChange={(value) => updateNodeData(selectedNode.id, { outOfHoursMessage: value })}
                                                            placeholder="Digite a mensagem enviada quando estiver fora do expediente..."
                                                            label="Mensagem fora do expediente"
                                                            rows={3}
                                                            maxLength={1000}
                                                            showPreview={true}
                                                            showVariables={true}
                                                            variables={FLOW_BOT_VARIABLES}
                                                        />
                                                        <Stack direction="row" spacing={1}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<CloseIcon />}
                                                                onClick={() => {
                                                                    updateNodeData(selectedNode.id, { outOfHoursMessage: '' });
                                                                    setEditingOutOfHoursNodeId(null);
                                                                }}
                                                            >
                                                                Usar mensagem da configuração
                                                            </Button>
                                                            <Button size="small" variant="outlined" onClick={() => setEditingOutOfHoursNodeId(null)}>
                                                                Concluído
                                                            </Button>
                                                        </Stack>
                                                    </Stack>
                                                ) : (
                                                    <Stack spacing={1}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Mensagem que será enviada (da configuração escolhida ou personalizada):
                                                        </Typography>
                                                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', minHeight: 60 }}>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                {displayMessage || 'Nenhuma mensagem configurada na origem. Clique em Editar para definir.'}
                                                            </Typography>
                                                        </Paper>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<EditIcon />}
                                                            onClick={() => setEditingOutOfHoursNodeId(selectedNode.id)}
                                                        >
                                                            Editar
                                                        </Button>
                                                    </Stack>
                                                );
                                            })()}
                                        </Stack>
                                    )}

                                    {selectedNode.data.type === 'stopBot' && (
                                        <SysEditorWhatsapp
                                            value={selectedNode.data.text || ''}
                                            onChange={(value) => updateNodeData(selectedNode.id, { text: value })}
                                            placeholder="Digite a mensagem de encerramento (opcional)..."
                                            label="Mensagem de Encerramento (Opcional)"
                                            rows={3}
                                            maxLength={1000}
                                            showPreview={true}
                                            helperText="Mensagem enviada antes de desativar o bot."
                                            showVariables={true}
                                            variables={FLOW_BOT_VARIABLES}
                                        />
                                    )}
                                    {(selectedNode.data.type === 'closedTicketByBot' || selectedNode.type === 'closedTicketByBot') && (
                                        <>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Mensagem enviada antes de encerrar o atendimento e marcar o ticket como encerrado pelo bot.
                                            </Typography>
                                            <SysEditorWhatsapp
                                                value={selectedNode.data.text || ''}
                                                onChange={(value) => updateNodeData(selectedNode.id, { text: value })}
                                                placeholder="Digite a mensagem de encerramento (opcional)..."
                                                label="Mensagem de Encerramento (Opcional)"
                                                rows={3}
                                                maxLength={1000}
                                                showPreview={true}
                                                helperText="Se vazio, nenhuma mensagem é enviada."
                                                showVariables={true}
                                                variables={FLOW_BOT_VARIABLES}
                                            />
                                        </>
                                    )}

                                    {selectedNode.data.type === 'httpRequest' && (
                                        <>
                                            <TextField
                                                label="URL do Webhook"
                                                fullWidth
                                                value={selectedNode.data.url || ''}
                                                onChange={(e) => updateNodeData(selectedNode.id, { url: e.target.value })}
                                                placeholder="https://exemplo.com/webhook"
                                                size="small"
                                                helperText="URL completa do webhook que será chamado"
                                            />
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Método HTTP</InputLabel>
                                                <Select
                                                    value={selectedNode.data.method || 'GET'}
                                                    label="Método HTTP"
                                                    onChange={(e) => updateNodeData(selectedNode.id, { method: e.target.value })}
                                                >
                                                    <MenuItem value="GET">GET</MenuItem>
                                                    <MenuItem value="POST">POST</MenuItem>
                                                    <MenuItem value="PUT">PUT</MenuItem>
                                                    <MenuItem value="PATCH">PATCH</MenuItem>
                                                    <MenuItem value="DELETE">DELETE</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {(selectedNode.data.method === 'POST' || selectedNode.data.method === 'PUT' || selectedNode.data.method === 'PATCH') && (
                                                <TextField
                                                    label="Body (JSON)"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    value={selectedNode.data.body || ''}
                                                    onChange={(e) => updateNodeData(selectedNode.id, { body: e.target.value })}
                                                    placeholder='{"key": "value"}'
                                                    size="small"
                                                    helperText="Corpo da requisição em formato JSON (opcional). Use {{variavel}} para variáveis."
                                                />
                                            )}
                                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Headers (Opcional)</Typography>
                                            <Stack spacing={1}>
                                                {(selectedNode.data.headers || []).map((header: any, index: number) => (
                                                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <TextField
                                                            size="small"
                                                            label="Chave"
                                                            value={header.key || ''}
                                                            onChange={(e) => {
                                                                const newHeaders = [...(selectedNode.data.headers || [])];
                                                                newHeaders[index] = { ...newHeaders[index], key: e.target.value };
                                                                updateNodeData(selectedNode.id, { headers: newHeaders });
                                                            }}
                                                            placeholder="ex: Authorization"
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            label="Valor"
                                                            value={header.value || ''}
                                                            onChange={(e) => {
                                                                const newHeaders = [...(selectedNode.data.headers || [])];
                                                                newHeaders[index] = { ...newHeaders[index], value: e.target.value };
                                                                updateNodeData(selectedNode.id, { headers: newHeaders });
                                                            }}
                                                            placeholder="ex: Bearer token123"
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const newHeaders = selectedNode.data.headers.filter((_: any, i: number) => i !== index);
                                                                updateNodeData(selectedNode.id, { headers: newHeaders });
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Button
                                                    startIcon={<AddCircleIcon />}
                                                    onClick={() => {
                                                        const newHeaders = [...(selectedNode.data.headers || []), { key: '', value: '' }];
                                                        updateNodeData(selectedNode.id, { headers: newHeaders });
                                                    }}
                                                    size="small"
                                                    variant="outlined"
                                                >
                                                    Adicionar Header
                                                </Button>
                                            </Stack>
                                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Mapeamento de Variáveis</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                Defina quais chaves do JSON de resposta serão salvas em variáveis
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(selectedNode.data.variableMappings || []).map((mapping: any, index: number) => (
                                                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <TextField
                                                            size="small"
                                                            label="Chave JSON"
                                                            value={mapping.jsonKey || ''}
                                                            onChange={(e) => {
                                                                const newMappings = [...(selectedNode.data.variableMappings || [])];
                                                                newMappings[index] = { ...newMappings[index], jsonKey: e.target.value };
                                                                updateNodeData(selectedNode.id, { variableMappings: newMappings });
                                                            }}
                                                            placeholder="ex: nome"
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            label="Variável"
                                                            value={mapping.variableName || ''}
                                                            onChange={(e) => {
                                                                const newMappings = [...(selectedNode.data.variableMappings || [])];
                                                                newMappings[index] = { ...newMappings[index], variableName: e.target.value };
                                                                updateNodeData(selectedNode.id, { variableMappings: newMappings });
                                                            }}
                                                            placeholder="ex: nome_cliente"
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const newMappings = selectedNode.data.variableMappings.filter((_: any, i: number) => i !== index);
                                                                updateNodeData(selectedNode.id, { variableMappings: newMappings });
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Button
                                                    startIcon={<AddCircleIcon />}
                                                    onClick={() => {
                                                        const newMappings = [...(selectedNode.data.variableMappings || []), { jsonKey: '', variableName: '' }];
                                                        updateNodeData(selectedNode.id, { variableMappings: newMappings });
                                                    }}
                                                    size="small"
                                                    variant="outlined"
                                                >
                                                    Adicionar Mapeamento
                                                </Button>
                                            </Stack>
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    startIcon={testLoading ? <CircularProgress size={16} /> : <TestIcon />}
                                                    onClick={testHttpRequest}
                                                    disabled={testLoading || !selectedNode.data.url}
                                                    color="primary"
                                                >
                                                    {testLoading ? 'Testando...' : 'Testar Requisição'}
                                                </Button>
                                            </Box>
                                        </>
                                    )}
                                    {selectedNode.data.type === 'interval' && (
                                        <>
                                            <TextField
                                                label="Intervalo (segundos)"
                                                fullWidth
                                                type="number"
                                                value={selectedNode.data.seconds || 1}
                                                onChange={(e) => {
                                                    const seconds = Math.max(1, parseInt(e.target.value) || 1);
                                                    updateNodeData(selectedNode.id, { seconds });
                                                }}
                                                placeholder="1"
                                                size="small"
                                                helperText="Tempo em segundos que o fluxo aguardará antes de prosseguir"
                                                inputProps={{ min: 1, step: 1 }}
                                            />
                                        </>
                                    )}
                                </Stack>
                            </Box>
                            <Divider />
                            <Box sx={{ p: 2 }}>
                                <Stack spacing={1}>

                                    {selectedNode.id !== 'start' ? (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<CopyIcon />}
                                                onClick={() => copyNode(selectedNode.id)}
                                            >
                                                Copiar Nó
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => deleteNode(selectedNode.id)}
                                            >
                                                Excluir Nó
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary" align="center" display="block">
                                            O nó de início é obrigatório e não pode ser excluído.
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </Box>

            {selectedNode && selectedNode.type === 'singleBlock' && (
                <FlowBuilderSingleBlockModal
                    open={singleBlockModalOpen}
                    onClose={() => setSingleBlockModalOpen(false)}
                    data={selectedNode.data}
                    onSave={(newData: any) => updateNodeData(selectedNode.id, newData)}
                />
            )}

            {selectedNode && (selectedNode.type === 'consultarFaturas' || selectedNode.data?.type === 'consultarFaturas') && (
                <FlowBuilderConsultarFaturasModal
                    open={consultarFaturasModalOpen}
                    onClose={() => setConsultarFaturasModalOpen(false)}
                    data={selectedNode.data}
                    onSave={(newData: any) => updateNodeData(selectedNode.id, newData)}
                />
            )}

            {selectedNode && (selectedNode.type === 'identifyClientV2' || selectedNode.data?.type === 'identifyClientV2') && (
                <FlowBuilderIdentifyClientV2Modal
                    open={identifyClientV2ModalOpen}
                    onClose={() => setIdentifyClientV2ModalOpen(false)}
                    data={selectedNode.data}
                    onSave={(newData: any) => updateNodeData(selectedNode.id, newData)}
                />
            )}

            {selectedNode && (selectedNode.type === 'consultarFaturasV2' || selectedNode.data?.type === 'consultarFaturasV2') && (
                <FlowBuilderConsultarFaturasV2Modal
                    open={consultarFaturasV2ModalOpen}
                    onClose={() => setConsultarFaturasV2ModalOpen(false)}
                    data={selectedNode.data}
                    onSave={(newData: any) => updateNodeData(selectedNode.id, newData)}
                />
            )}

            <FlowSimulator
                open={simulatorOpen}
                onClose={() => setSimulatorOpen(false)}
                nodes={nodes}
                edges={edges}
            />

            {/* Dialog de Teste HTTP */}
            <Dialog
                open={testDialogOpen}
                onClose={() => {
                    if (!testLoading) {
                        setTestDialogOpen(false);
                        setTestResult(null);
                        setTestError(null);
                    }
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Resultado do Teste HTTP
                    <IconButton
                        onClick={() => {
                            if (!testLoading) {
                                setTestDialogOpen(false);
                                setTestResult(null);
                                setTestError(null);
                            }
                        }}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        disabled={testLoading}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {testLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Executando requisição...</Typography>
                        </Box>
                    ) : testError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Erro:</Typography>
                            <Typography variant="body2">{testError}</Typography>
                        </Alert>
                    ) : testResult ? (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Status: {testResult.status} {testResult.statusText}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Tempo de resposta: {testResult.responseTime}ms
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Headers da Resposta:
                                </Typography>
                                <Paper sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: '0.75rem', overflow: 'auto', maxHeight: 150 }}>
                                    <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', m: 0 }}>
                                        {JSON.stringify(testResult.headers, null, 2)}
                                    </Typography>
                                </Paper>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Corpo da Resposta:
                                </Typography>
                                <Paper sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: '0.75rem', overflow: 'auto', maxHeight: 300 }}>
                                    <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', m: 0 }}>
                                        {typeof testResult.data === 'object'
                                            ? JSON.stringify(testResult.data, null, 2)
                                            : testResult.data}
                                    </Typography>
                                </Paper>
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setTestDialogOpen(false);
                            setTestResult(null);
                            setTestError(null);
                        }}
                        disabled={testLoading}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export const FlowEditor = () => (
    <ReactFlowProvider>
        <FlowEditorInner />
    </ReactFlowProvider>
);
